import { Component, OnDestroy, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import {
  AccountServiceBase,
  IdentityServiceBase,
  LoginModel,
  PasswordLoginModel,
} from '@seahorse/auth';
import { NotificationService } from '@seahorse/common';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  LanguageSelectComponent,
  LoadingIndicatorComponent,
  MfaService,
} from '@seahorse/ui';
import { MfaSetupCode } from '@seahorse/domain';
import { MfaSetupComponent } from '@seahorse/ui';
import { LoginResponse } from '@seahorse/auth';
import { SIDEBAR_LOCAL_STORAGE } from '@seahorse/layout';
import { SuEnvService } from '../shared/services/env.service';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { AuthLayoutComponent } from '@seahorse/shell';
import {
  ShButtonComponent,
  ShCheckboxComponent,
  ShInputComponent,
  ShPasswordComponent,
  ShMessageComponent,
} from '@seahorse/components';

@Component({
  selector: 'su-account-login',
  templateUrl: './login.component.html',
  imports: [
    CommonModule,
    MfaSetupComponent,
    TranslateModule,
    FormsModule,
    LoadingIndicatorComponent,
    AuthLayoutComponent,
    LanguageSelectComponent,
    ShButtonComponent,
    ShCheckboxComponent,
    ShInputComponent,
    ShPasswordComponent,
    ShMessageComponent,
  ],
})
export class LoginComponent implements OnDestroy {
  @ViewChild('mfaSetup', { static: false })
  mfaSetupComponent?: MfaSetupComponent;

  // No feature lines in the brand panel (review 2026-09-29), and
  // no version: the app has none (package.json says 1.0.0, and the build
  // timestamp doesn't count).

  isLoading = false;
  loginModel: LoginModel;
  loginResponse: LoginResponse;
  currentForm: FormType = 0;
  isLoggedIn: boolean;
  verificationCode?: string;
  mfaSetupCode?: MfaSetupCode;
  redirecting = false;

  private _subscriptions = new Subscription();
  private destroy$ = new Subject();

  constructor(
    private _accountService: AccountServiceBase,
    private _notificationService: NotificationService,
    private _identityService: IdentityServiceBase,
    private _envService: SuEnvService,
    private _mfaService: MfaService,
    public router: Router
  ) {
    const storedUrl = localStorage.getItem('mfaSetupInProgress');
    if (storedUrl) {
      this.router.navigateByUrl(storedUrl);
    }

    this.loginModel = new PasswordLoginModel();
    // Super-user only: "Remember me" starts checked (the shared LoginModel
    // default stays false for the customer apps).
    this.loginModel.rememberMe = true;

    this._accountService.accountActivatedCalled$
      .pipe(takeUntil(this.destroy$))
      .subscribe((showNotification: boolean) => {
        if (showNotification) {
          this._notificationService.showSuccess(
            'Account activated!',
            'shared.terms.success'
          );
        }
      });
  }

  login() {
    this.isLoading = true;
    this._subscriptions.add(
      this._accountService.login(this.loginModel).subscribe(
        (response) => {
          if (response.isSuccess) {
            localStorage.removeItem(SIDEBAR_LOCAL_STORAGE.isOpen);

            if (!response.isMfaEnabled && response.isMfaRequired) {
              this._mfaService.initMfa().subscribe((res) => {
                this.mfaSetupCode = res.result;
                this.currentForm = FormType.SetupMFA;
              });
            } else if (response.isMfaEnabled) {
              this.currentForm = FormType.MFA;
            } else {
              this.router.navigate(['/']);
            }
          }
        },
        (error) => {
          this.loginResponse = {} as LoginResponse;

          if (error.status === 401) {
            this.loginResponse.isUnauthorized = true;
          }
          this.isLoading = false;
        },
        () => {
          this.isLoading = false;
        }
      )
    );
  }

  sendVerificationCode(code: string) {
    this.isLoading = true;
    this._accountService.verifyMfa(code).subscribe((response) => {
      if (response.isSuccess) {
        this.isLoading = false;
        this.router.navigate(['/']);
        this.redirecting = true;
      } else {
        this.isLoading = false;
        this._notificationService.showError('user.mfa.invalidVerificationCode');
      }
    });
  }

  completeMfa() {
    if (!this.mfaSetupComponent) {
      return;
    }

    const code = this.mfaSetupComponent.codeControl.getRawValue();

    if (!code.length) {
      this._notificationService.showError('user.mfa.verificationCodeRequired');

      return;
    }

    this._mfaService.completeMfa(code, true).subscribe((res) => {
      if (res.result) {
        this._identityService.isMfaEnabled = true;
        this._notificationService.showSuccess(
          'user.mfa.enabled',
          'shared.terms.success'
        );
        this.router.navigate(['/']);
        this.redirecting = true;
      } else {
        this._notificationService.showError(
          'user.mfa.invalidVerificationCode',
          'shared.terms.error'
        );
      }
    });
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}

export enum FormType {
  Login,
  ForgotPassword,
  MFA,
  SetupMFA,
}
